import React, { FC, useMemo } from 'react';
import styled from 'styled-components/native';

import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { useTheme } from '@webtrader/core/services/theme/v2';

import MaterialIcons from '@/components/Icons/Material';
import Text from '@/components/Text';

import { DEFAULT_BORDERED_INPUT_STYLES, SMALL_BORDERED_INPUT_STYLES } from '../../Settings';

export type ActionSheetSize = 'default' | 'small';

interface IActionSheetSelectorProps {
  withBorder?: boolean;
  size?: ActionSheetSize;
  title?: string;
  label?: string;
  disabled?: boolean;
  justifyCenter?: boolean;
  testID?: EDataTestId;
  placeholder?: string;
}

const ActionSheetSelector: FC<IActionSheetSelectorProps> = (props) => {
  const { withBorder, size, title, label, justifyCenter, testID, placeholder } = props;

  const { theme } = useTheme();

  const { height, iconSize, fontSize } = useMemo(() => {
    switch (size) {
      case 'small':
        return {
          height: SMALL_BORDERED_INPUT_STYLES.height,
          iconSize: 22,
          fontSize: SMALL_BORDERED_INPUT_STYLES.fontSize
        };
      default:
        return {
          height: DEFAULT_BORDERED_INPUT_STYLES.height + dynamicSize.convertSize(2),
          iconSize: 26,
          fontSize: DEFAULT_BORDERED_INPUT_STYLES.fontSize
        };
    }
  }, [size]);

  const fieldText = label ? label : placeholder || '';

  return (
    <SelectorField>
      {!!title && <SelectorFieldTitle>{title}</SelectorFieldTitle>}
      <Selector height={height} withBorder={withBorder} testID={testID}>
        <StyledSelectorLabel size={fontSize} justifyCenter={justifyCenter} isDisplayPlaceHolder={!label}>
          {fieldText}
        </StyledSelectorLabel>
        <MaterialIcons name="arrow-drop-down" size={iconSize} color={theme.colors.text[1]} />
      </Selector>
    </SelectorField>
  );
};

export default ActionSheetSelector;

const SelectorField = styled.View``;

const Selector = styled.View<{ height: number; withBorder?: boolean }>`
  height: ${({ height }) => height}px;
  padding-start: ${DEFAULT_BORDERED_INPUT_STYLES.paddingStart}px;
  padding-end: ${DEFAULT_BORDERED_INPUT_STYLES.paddingEnd - dynamicSize.convertSize(4)}px;

  align-items: center;
  flex-direction: row;
  justify-content: space-between;

  border: ${({ withBorder }) => (withBorder ? 1 : 0)}px solid ${({ theme }) => theme.colors.border[1]};
  border-radius: ${DEFAULT_BORDERED_INPUT_STYLES.borderRadius}px;
`;

const SelectorFieldTitle = styled(Text)`
  text-align: left;
  font-size: ${DEFAULT_BORDERED_INPUT_STYLES.label.fontSize}px;
  font-weight: ${DEFAULT_BORDERED_INPUT_STYLES.label.fontWeight};

  padding-bottom: ${DEFAULT_BORDERED_INPUT_STYLES.label.paddingBottom}px;
`;

const StyledSelectorLabel = styled(Text)<{
  size: number;
  disable?: boolean;
  justifyCenter?: boolean;
  isDisplayPlaceHolder?: boolean;
}>`
  ${({ justifyCenter }) => justifyCenter && 'margin: auto;'}
  font-size: ${({ size }) => size}px;
  color: ${({ theme, disable, isDisplayPlaceHolder }) => {
    const textColors = theme.colors.text;

    if (isDisplayPlaceHolder) {
      return textColors[2];
    }
    return disable ? textColors[1] : textColors[0];
  }};
`;
